import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface GuestContextType {
  isGuest: boolean;
  guestData: any;
  setGuestMode: (enabled: boolean) => void;
  saveGuestData: (key: string, data: any) => void;
  getGuestData: (key: string) => any;
  clearGuestData: () => void;
  showLoginPrompt: boolean;
  setShowLoginPrompt: (show: boolean) => void;
}

const GuestContext = createContext<GuestContextType | undefined>(undefined);

export function GuestProvider({ children }: { children: ReactNode }) {
  const [isGuest, setIsGuest] = useState(false);
  const [guestData, setGuestData] = useState<any>({});
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    // Load guest mode from localStorage
    const guestMode = localStorage.getItem('guest_mode') === 'true';
    setIsGuest(guestMode);

    // Load guest data
    const savedData = localStorage.getItem('guest_data');
    if (savedData) {
      try {
        setGuestData(JSON.parse(savedData));
      } catch (e) {
        console.error('Failed to parse guest data');
      }
    }
  }, []);

  const setGuestMode = (enabled: boolean) => {
    setIsGuest(enabled);
    localStorage.setItem('guest_mode', enabled.toString());
  };

  const saveGuestData = (key: string, data: any) => {
    const newData = { ...guestData, [key]: data };
    setGuestData(newData);
    localStorage.setItem('guest_data', JSON.stringify(newData));
  };

  const getGuestData = (key: string) => {
    return guestData[key];
  };

  const clearGuestData = () => {
    setGuestData({});
    localStorage.removeItem('guest_data');
    localStorage.removeItem('guest_mode');
  };

  return (
    <GuestContext.Provider
      value={{
        isGuest,
        guestData,
        setGuestMode,
        saveGuestData,
        getGuestData,
        clearGuestData,
        showLoginPrompt,
        setShowLoginPrompt,
      }}
    >
      {children}
    </GuestContext.Provider>
  );
}

export function useGuest() {
  const context = useContext(GuestContext);
  if (context === undefined) {
    throw new Error('useGuest must be used within a GuestProvider');
  }
  return context;
}
